<template>
    <div class="cams">
        <div class="cams__title">
            <span class="cams__title-icon"><v-icon>$cctv</v-icon></span>
            {{ $t('msg.cameras') }}
        </div>
        <div class="cams__header">
            <div class="cams__items">
                <div
                        v-for="(item, key) in cams"
                        :key="key"
                        class="cams__item"
                >
                    <img :src="item.icon" @click="openCam(item.code)" class="cams__icon">
                    <div class="cams__item-bottom">
                        <div>
                            <div class="cams__label">{{ item.code }}</div>
                            <div class="cams__history">
                                {{ $t('msg.archiveHistory') }}: {{ formatArchiveDays(item.archive_days) }}
                            </div>
                        </div>
                        <v-chip
                                small
                                color="primary"
                                class="cams__play"
                                @click="stream(item.stream_path)"
                        >online <v-icon>$play</v-icon></v-chip>
                    </div>
                </div>
                <v-btn
                        icon
                        class="primary cams__refresh"
                        small
                        @click="loadCams()"
                        :loading="camsLoading"
                >
                    <v-icon>$refresh</v-icon>
                </v-btn>
            </div>
        </div>
        <div class="cams__content">
            <div class="cams__content-header">
                <div class="cams__content-title">
                    <span class="cams__title-icon"><v-icon>$history</v-icon></span>
                    {{ $t('msg.records') }}
                </div>
                <div class="cams__filters">
                    <v-autocomplete
                            v-model="hour"
                            :items="hourLabels"
                            outlined
                            hide-details="auto"
                            style="width: 150px"
                            class="mr-4"
                            :label="$t('msg.hour')"
                            clearable
                    >
                        <template v-slot:selection="{item}">
                            {{ item }}:00
                        </template>
                        <template v-slot:item="{item}">
                            {{ item }}:00
                        </template>
                    </v-autocomplete>
                    <date-field
                            v-model="date"
                            style="width: 150px"
                            :label="$t('msg.date')"
                            @input="loadHours()"
                    ></date-field>
                    <v-btn
                        icon
                        class="primary ml-4"
                        small
                        @click="loadHours()"
                        :loading="hoursLoading"
                    >
                        <v-icon>$refresh</v-icon>
                    </v-btn>
                </div>
            </div>
            <div
                    v-for="(hour, key) in hoursFiltered"
                    :key="key"
                    class="cams__hour">
                <div class="cams__hour-label">
                    {{ hour.name }}:00
                </div>
                <div class="cams__hour-wrap">
                    <div
                            v-for="(item, key) in hour.videos"
                            :key="key"
                            class="cams__item"
                    >
                        <button
                                v-if="item.recording"
                                type="button"
                                class="cams__recording"
                                @click="openCurrentStream"
                        >
                            <span class="cams__recording-dot"></span>
                            <v-icon>$recordRec</v-icon>
                            <span>{{ $t('msg.recording') }}</span>
                        </button>
                        <img v-else :src="item.icon" class="cams__icon" @click="play(item.video)">
                        <div class="cams__item-bottom">
                            <div class="cams__label">
                                {{ item.time }}
                            </div>
                            <div class="cams__size">
                                {{ item.video_weight | numFormat('0,0.00') }} MB
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <section class="server-stats">
            <div class="server-stats__header">
                <div class="server-stats__title">
                    <span class="cams__title-icon"><v-icon>$server</v-icon></span>
                    {{ $t('msg.server') }}
                </div>
                <v-btn
                        icon
                        small
                        :loading="serverStatsLoading"
                        @click="loadServerStats"
                        class="server-stats__refresh"
                >
                    <v-icon>$refresh</v-icon>
                </v-btn>
            </div>
            <div class="server-stats__grid">
                <article class="server-stat">
                    <div class="server-stat__icon server-stat__icon--archive"><v-icon>$videoBox</v-icon></div>
                    <div class="server-stat__content">
                        <div class="server-stat__label">{{ $t('msg.archiveForDay') }}</div>
                        <div class="server-stat__value">{{ formatArchiveSize(sumWeight) }}</div>
                        <div class="server-stat__meta">{{ date }} · {{ code }}</div>
                    </div>
                </article>
                <template v-if="serverStats">
                <article class="server-stat">
                    <div class="server-stat__icon server-stat__icon--load"><v-icon>$speedometer</v-icon></div>
                    <div class="server-stat__content">
                        <div class="server-stat__label">{{ $t('msg.cpuLoad') }}</div>
                        <div class="server-stat__value">{{ formatPercent(serverStats.cpu_used_percent) }}%</div>
                        <div class="server-stat__meta">
                            {{ serverStats.cpu_cores }} CPU · {{ formatPercent(100 - serverStats.cpu_used_percent) }}% {{ $t('msg.free') }}
                        </div>
                        <v-progress-linear
                                :value="serverStats.cpu_used_percent"
                                height="5"
                                rounded
                                :color="cpuLoadColor(serverStats.cpu_used_percent)"
                                background-color="#e2e8f0"
                                class="server-stat__progress"
                        ></v-progress-linear>
                    </div>
                </article>
                <article class="server-stat">
                    <div class="server-stat__icon server-stat__icon--disk"><v-icon>$harddisk</v-icon></div>
                    <div class="server-stat__content">
                        <div class="server-stat__label">{{ $t('msg.diskFree') }}</div>
                        <div class="server-stat__value">{{ formatBytes(serverStats.disk.free) }}</div>
                        <div class="server-stat__meta">
                            {{ formatBytes(serverStats.disk.used) }} {{ $t('msg.usedOf') }} {{ formatBytes(serverStats.disk.total) }}
                        </div>
                        <v-progress-linear
                                :value="serverStats.disk.used_percent"
                                height="5"
                                rounded
                                :color="diskUsageColor(serverStats.disk.used_percent)"
                                background-color="#e2e8f0"
                                class="server-stat__progress"
                        ></v-progress-linear>
                    </div>
                </article>
                <article class="server-stat">
                    <div class="server-stat__icon server-stat__icon--memory"><v-icon>$memory</v-icon></div>
                    <div class="server-stat__content">
                        <div class="server-stat__label">{{ $t('msg.memoryFree') }}</div>
                        <div class="server-stat__value">{{ formatBytes(serverStats.memory.free) }}</div>
                        <div class="server-stat__meta">
                            {{ formatBytes(serverStats.memory.used) }} {{ $t('msg.usedOf') }} {{ formatBytes(serverStats.memory.total) }}
                        </div>
                        <v-progress-linear
                                :value="serverStats.memory.used_percent"
                                height="5"
                                rounded
                                color="secondary"
                                background-color="#e2e8f0"
                                class="server-stat__progress"
                        ></v-progress-linear>
                    </div>
                </article>
                <article class="server-stat">
                    <div class="server-stat__icon server-stat__icon--uptime"><v-icon>$clockOutline</v-icon></div>
                    <div class="server-stat__content">
                        <div class="server-stat__label">{{ $t('msg.uptime') }}</div>
                        <div class="server-stat__value">{{ formatUptime(serverStats.uptime_seconds) }}</div>
                        <div class="server-stat__meta">{{ $t('msg.withoutRestart') }}</div>
                    </div>
                </article>
                </template>
            </div>
        </section>
        <v-dialog
            v-model="streamDialog"
            max-width="1280"
        >
            <div class="cams__stream">
                <img
                        v-if="streamPath"
                        :src="streamPath"
                        class="cams__stream-img"
                        @load="onStreamLoaded"
                        @error="onStreamError"
                >
                <div v-if="streamLoading || streamError" class="cams__stream-state">
                    <template v-if="streamLoading">
                        <v-progress-circular indeterminate color="white" size="42"></v-progress-circular>
                        <div>{{ streamRetryAttempt ? $t('msg.streamReconnecting') : $t('msg.streamLoading') }}</div>
                    </template>
                    <template v-else>
                        <v-icon>$videoOffOutline</v-icon>
                        <div>{{ $t('msg.streamUnavailable') }}</div>
                        <v-btn small color="primary" @click="retryStream">{{ $t('msg.retry') }}</v-btn>
                    </template>
                </div>
            </div>
        </v-dialog>
        <v-dialog
                v-model="playDialog"
                max-width="1000"
        >
            <div class="cams__stream">
                <video controls autoplay :src="playPath" ref="player" class="cams__stream-video"></video>
            </div>
        </v-dialog>
    </div>
</template>

<script>
    import DateField from '../../components/ui/Date';
    import { format } from 'date-fns';
    import VueScrollTo from "vue-scrollto";

    export default {
        components: {DateField},
        data() {
            return {
                cams: [],
                camsLoading: false,
                hours: [],
                hoursLoading: false,
                streamDialog: false,
                streamPath: null,
                streamBasePath: null,
                streamLoading: false,
                streamError: false,
                streamRetryAttempt: 0,
                streamRetryTimer: null,
                streamLoadTimer: null,
                playDialog: false,
                playPath: null,
                date: format(new Date(), 'yyyy-MM-dd'),
                hour: null,
                code: null,
                sumWeight: 0,
                serverStats: null,
                serverStatsLoading: false,
                serverStatsTimer: null,
                hourLabels: ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11',
                    '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23']
            }
        },
        computed: {
            hoursFiltered() {
                let ret = {};

                if (this.hour === undefined || this.hour === null) return this.hours;

                for (let key in this.hours) {
                    if (this.hours[key].name === this.hour) {
                        ret[key] = this.hours[key];
                    }
                }

                return ret;
            }
        },
        watch: {
            streamDialog(v) {
                if (!v) {
                    this.clearStreamRetry();
                    this.streamPath = null
                    this.streamBasePath = null;
                    this.streamLoading = false;
                    this.streamError = false;
                    this.streamRetryAttempt = 0;
                }
            },
            playDialog(v) {
                if (!v) {
                    this.playPath = null
                }
            }
        },
        methods: {
            loadCams() {
                if (this.camsLoading) return;
                this.camsLoading = true;
                this.$store.dispatch('loadCams')
                    .then(res => {

                        let cams = [];

                        res.forEach(cam => {

                            if (this.getPermission('cam_'+cam.code)) {
                                cams.push(cam);
                            }
                        });

                        this.cams = cams;
                        if (res.length > 0 && this.cams.length > 0) {
                            if (this.code === null) this.code = this.cams[0].code;
                            this.loadHours();
                        }

                        if (this.$route.query.code !== undefined && this.$route.query.online === null) {

                            let item = this.cams.find(v => v.code === this.$route.query.code);

                            if (item !== undefined) {
                                this.stream(item.stream_path);
                            }
                        }
                    })
                    .finally(() => this.camsLoading = false);
            },
            loadHours() {
                if (this.hoursLoading) return;
                this.hoursLoading = true;
                const params = {
                    date: this.date,
                };
                this.$store.dispatch('loadVideos', {id: this.code, params})
                    .then(res => {
                        let sumWeight = 0;
                        let hour = null;

                        res.forEach(_hour => {
                            hour = _hour.name;

                            _hour.videos.forEach(video => {
                                sumWeight += video.video_weight;
                            })
                        });

                        if (this.hour === undefined || this.hour === null) this.hour = hour;
                        this.hours = res;
                        this.sumWeight = sumWeight;
                    })
                    .finally(() => this.hoursLoading = false);
            },
            stream(streamPath) {
                this.clearStreamRetry();
                this.streamBasePath = streamPath;
                this.streamRetryAttempt = 0;
                this.streamDialog = true;
                this.connectStream();
            },
            connectStream() {
                if (!this.streamDialog || !this.streamBasePath) return;
                this.clearStreamLoadTimer();
                this.streamLoading = true;
                this.streamError = false;
                const separator = this.streamBasePath.includes('?') ? '&' : '?';
                this.streamPath = this.streamBasePath + separator + '_stream=' + Date.now();
                // Some browsers do not emit load for an endless MJPEG response.
                // Stop covering a valid stream after its first frames had time to arrive.
                this.streamLoadTimer = setTimeout(() => {
                    if (this.streamPath && !this.streamError) this.streamLoading = false;
                }, 3000);
            },
            onStreamLoaded() {
                this.clearStreamLoadTimer();
                this.streamLoading = false;
                this.streamError = false;
                this.streamRetryAttempt = 0;
            },
            onStreamError() {
                if (!this.streamDialog) return;
                this.clearStreamLoadTimer();
                this.streamPath = null;
                this.streamLoading = false;
                this.streamError = true;
                this.streamRetryAttempt += 1;
                this.clearStreamRetry();
                const delay = Math.min(2000 * this.streamRetryAttempt, 10000);
                this.streamRetryTimer = setTimeout(() => this.connectStream(), delay);
            },
            retryStream() {
                this.clearStreamRetry();
                this.connectStream();
            },
            clearStreamRetry() {
                if (this.streamRetryTimer) {
                    clearTimeout(this.streamRetryTimer);
                    this.streamRetryTimer = null;
                }
                this.clearStreamLoadTimer();
            },
            clearStreamLoadTimer() {
                if (this.streamLoadTimer) {
                    clearTimeout(this.streamLoadTimer);
                    this.streamLoadTimer = null;
                }
            },
            openCam(code) {
                this.code = code;
                this.loadHours();

                let el = this.$el.querySelector('.cams__content');

                VueScrollTo.scrollTo(el, 700, {
                    offset: -70,
                });
            },
            play(playPath) {
                this.playPath = playPath;
                this.playDialog = true;

                setTimeout(() => {
                    if (this.$refs.player !== undefined) this.$refs.player.play();
                }, 300);
            },
            loadServerStats() {
                if (this.serverStatsLoading) return;
                this.serverStatsLoading = true;
                this.$store.dispatch('loadServerStats')
                    .then(stats => this.serverStats = stats)
                    .finally(() => this.serverStatsLoading = false);
            },
            formatPercent(value) {
                return Math.max(0, Math.min(100, Number(value))).toFixed(0);
            },
            cpuLoadColor(value) {
                if (value >= 85) return 'error';
                if (value >= 65) return 'secondary';
                return 'primary';
            },
            diskUsageColor(value) {
                if (value >= 90) return 'error';
                if (value >= 80) return 'secondary';
                return 'primary';
            },
            openCurrentStream() {
                const cam = this.cams.find(item => item.code === this.code);
                if (cam) this.stream(cam.stream_path);
            },
            formatArchiveDays(days) {
                const value = Number(days) || 0;
                if (this.$i18n.locale !== 'ru') return value + (value === 1 ? ' day' : ' days');
                const lastTwo = value % 100;
                const last = value % 10;
                if (lastTwo >= 11 && lastTwo <= 14) return value + ' дней';
                if (last === 1) return value + ' день';
                if (last >= 2 && last <= 4) return value + ' дня';
                return value + ' дней';
            },
            formatBytes(bytes) {
                if (!bytes) return '0 B';
                const units = ['B', 'KB', 'MB', 'GB', 'TB'];
                const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
                const value = bytes / Math.pow(1024, index);
                return value.toFixed(index >= 3 ? 1 : 0) + ' ' + units[index];
            },
            formatArchiveSize(megabytes) {
                if (megabytes >= 1024) return (megabytes / 1024).toFixed(2) + ' GB';
                return Number(megabytes).toFixed(2) + ' MB';
            },
            formatUptime(seconds) {
                const days = Math.floor(seconds / 86400);
                const hours = Math.floor((seconds % 86400) / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);

                if (days > 0) return days + ' ' + this.$t('msg.daysShort') + ' ' + hours + ' ' + this.$t('msg.hoursShort');
                if (hours > 0) return hours + ' ' + this.$t('msg.hoursShort') + ' ' + minutes + ' ' + this.$t('msg.minutesShort');
                return minutes + ' ' + this.$t('msg.minutesShort');
            }
        },
        created() {
            this.loadCams();
            this.loadServerStats();
            this.serverStatsTimer = setInterval(this.loadServerStats, 30000);
        },
        mounted() {
            if (this.$route.query.code !== undefined) {
                this.openCam(this.$route.query.code);
            }
        },
        beforeDestroy() {
            clearInterval(this.serverStatsTimer);
            this.clearStreamRetry();
        }
    }
</script>

<style lang="scss">
    .cams {
        .cams__title {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #17212b;
            font-size: 22px;
            font-weight: 700;
            letter-spacing: -.3px;
            margin-bottom: 16px;
        }
        .cams__title-icon {
            width: 36px;
            height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            border-radius: 11px;
            background: rgba(15, 159, 143, .1);

            .v-icon {
                color: #0f9f8f;
                font-size: 20px;
            }
        }
        .cams__header {
            padding: 20px;
            border: 1px solid #e2e8f0;
            border-radius: 18px;
            background: rgba(255, 255, 255, .88);
            box-shadow: 0 10px 30px rgba(15, 23, 42, .05);
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }
        .cams__items {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 320px));
            gap: 16px;
            width: 100%;
        }
        .cams__item {
            width: 100%;
            position: relative;
            overflow: hidden;
            font-size: 0;
            border-radius: 14px;
            background: #dfe7eb;
            box-shadow: 0 8px 22px rgba(15, 23, 42, .11);
            transition: transform .2s ease, box-shadow .2s ease;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 14px 28px rgba(15, 23, 42, .16);
            }
            img {
                display: block;
                width: 100%;
                height: 180px;
                object-fit: cover;
            }
        }
        .cams__item-bottom {
            width: 100%;
            position: absolute;
            bottom: 0;
            left: 0;
            min-height: 46px;
            color: #fff;
            background: linear-gradient(180deg, transparent, rgba(8, 15, 24, .92));
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .cams__label {
            font-size: 14px;
            font-weight: 600;
            padding: 14px 12px 0;
        }
        .cams__history {
            padding: 1px 12px 8px;
            color: rgba(255, 255, 255, .76);
            font-size: 11px;
            line-height: 1.3;
        }
        .cams__play {
            height: 28px;
            cursor: pointer;
            margin: 8px;
            padding: 8px 4px 8px 10px;
            color: #fff;
            font-size: 12px;
            font-weight: 600;

            .v-icon {
                font-size: 18px;
            }
        }
        .cams__icon {
            cursor: pointer;
        }
        .cams__recording {
            display: flex;
            width: 100%;
            min-height: 132px;
            align-items: center;
            justify-content: center;
            gap: 8px;
            border: 0;
            color: #fff;
            background: linear-gradient(145deg, #263746, #101922);
            cursor: pointer;
            font: inherit;
            font-size: 14px;
            font-weight: 600;

            .v-icon {
                color: rgba(255, 255, 255, .82);
                font-size: 24px;
            }
        }
        .cams__recording-dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
            background: #ef4444;
            box-shadow: 0 0 0 0 rgba(239, 68, 68, .65);
            animation: recording-pulse 1.6s infinite;
        }
        .cams__refresh {
            flex: 0 0 auto;
            margin-left: 14px;
            border-radius: 10px;
            box-shadow: 0 7px 18px rgba(15, 159, 143, .2);
        }
        .cams__hour-label {
            min-width: 58px;
            font-size: 18px;
            font-weight: 700;
            color: #94a3b8;
        }
        .cams__hour {
            display: flex;
            margin-top: 22px;
        }
        .cams__hour-label {
            margin-top: 8px;
        }
        .cams__hour-wrap {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
            gap: 14px;
            width: 100%;
            margin-left: 18px;

            .cams__item {
                img {
                    height: 132px;
                }
            }
        }
        .cams__size {
            color: rgba(255, 255, 255, .78);
            font-size: 12px;
            margin-right: 12px;
        }
        .server-stats {
            margin-top: 34px;
        }
        .server-stats__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 14px;
            border-bottom: 1px solid #e2e8f0;
        }
        .server-stats__title {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #17212b;
            font-size: 22px;
            font-weight: 700;
        }
        .server-stats__refresh {
            color: #64748b;
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
        }
        .server-stats__grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
            gap: 14px;
            margin-top: 18px;
        }
        .server-stat {
            display: flex;
            gap: 13px;
            min-width: 0;
            padding: 17px;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            background: rgba(255, 255, 255, .9);
            box-shadow: 0 8px 24px rgba(15, 23, 42, .04);
        }
        .server-stat__icon {
            width: 38px;
            height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            border-radius: 11px;

            .v-icon {
                font-size: 20px;
            }
        }
        .server-stat__icon--load {
            background: rgba(15, 159, 143, .11);
            .v-icon { color: #0f9f8f; }
        }
        .server-stat__icon--archive {
            background: rgba(236, 72, 153, .11);
            .v-icon { color: #db2777; }
        }
        .server-stat__icon--disk {
            background: rgba(59, 130, 246, .11);
            .v-icon { color: #3b82f6; }
        }
        .server-stat__icon--memory {
            background: rgba(245, 158, 11, .13);
            .v-icon { color: #d97706; }
        }
        .server-stat__icon--uptime {
            background: rgba(139, 92, 246, .11);
            .v-icon { color: #8b5cf6; }
        }
        .server-stat__content {
            min-width: 0;
            flex: 1;
        }
        .server-stat__label {
            color: #64748b;
            font-size: 13px;
            font-weight: 600;
        }
        .server-stat__value {
            margin-top: 2px;
            color: #17212b;
            font-size: 21px;
            font-weight: 750;
            letter-spacing: -.3px;
        }
        .server-stat__meta {
            overflow: hidden;
            margin-top: 2px;
            color: #94a3b8;
            font-size: 12px;
            line-height: 1.45;
            text-overflow: ellipsis;
        }
        .server-stat__progress {
            margin-top: 10px;
        }
        .cams__content-header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            margin-top: 34px;
            padding-bottom: 14px;
            border-bottom: 1px solid #e2e8f0;
        }
        .cams__content-title {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #17212b;
            font-size: 22px;
            font-weight: 700;
        }
        .cams__filters {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 10px;

            .v-input {
                margin: 0 !important;
            }
            .v-input input,
            .v-select__selection,
            .v-select__selections {
                font-size: 16px !important;
            }
            .v-btn {
                margin-left: 2px !important;
                border-radius: 10px;
                box-shadow: 0 7px 18px rgba(15, 159, 143, .2);
            }
        }
    }
    .cams__stream {
        position: relative;
        width: 100%;
        min-height: 0;
        aspect-ratio: 16 / 9;
        font-size: 0;
        overflow: hidden;
        border-radius: 16px;
        background: #080f18;
        box-shadow: 0 24px 70px rgba(0, 0, 0, .35);
    }
    .cams__stream-img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .cams__stream-state {
        position: absolute;
        inset: 0;
        display: flex;
        min-height: 0;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 14px;
        padding: 24px;
        color: #fff;
        background: #080f18;
        font-size: 15px;
        text-align: center;

        .v-icon {
            color: rgba(255, 255, 255, .8);
            font-size: 44px;
        }
    }
    .cams__stream-video {
        width: 100%;
        height: 100%;
    }

    @keyframes recording-pulse {
        0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, .65); }
        70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
    }

    @media (max-width: 600px) {
        .cams {
            .cams__title,
            .cams__content-title {
                font-size: 20px;
            }
            .cams__header {
                padding: 12px;
            }
            .cams__items {
                grid-template-columns: 1fr;
            }
            .cams__item img {
                height: auto;
                aspect-ratio: 16 / 10;
            }
            .cams__hour-wrap .cams__item img {
                height: auto;
            }
            .cams__content-header {
                align-items: flex-start;
            }
            .cams__filters {
                margin-top: 18px;
            }
            .server-stats__title {
                font-size: 20px;
            }
            .server-stats__grid {
                grid-template-columns: 1fr;
            }
        }
    }

    @media (min-width: 601px) and (max-width: 1100px) {
        .cams .server-stats__grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }
</style>
